import React, { useCallback, useMemo } from 'react';
import { Currency } from '@cryptoalgebra/kim-sdk';
import { IDerivedMintInfo } from 'src/state/mintStore';
import { PresetProfits, Presets, PresetsArgs } from 'src/types/presets';
import { Button, HStack } from '@chakra-ui/react';
import {
  useLiquidityWizardState,
  useMintActionHandlers
} from '@app/screens/liquidity/LiquidityWizard/state';

interface RangeSidebarProps {
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
  mintInfo: IDerivedMintInfo;
}

const stablecoinsPreset = [
  {
    type: Presets.STABLE,
    title: `Stablecoins`,
    min: 0.984,
    max: 1.01,
    risk: PresetProfits.VERY_LOW,
    profit: PresetProfits.HIGH
  }
];

const commonPresets = [
  {
    type: Presets.RISK,
    title: `Narrow`,
    min: 0.95,
    max: 1.1,
    risk: PresetProfits.HIGH,
    profit: PresetProfits.HIGH
  },
  {
    type: Presets.NORMAL,
    title: `Common`,
    min: 0.9,
    max: 1.2,
    risk: PresetProfits.MEDIUM,
    profit: PresetProfits.MEDIUM
  },
  {
    type: Presets.SAFE,
    title: `Wide`,
    min: 0.8,
    max: 1.4,
    risk: PresetProfits.LOW,
    profit: PresetProfits.LOW
  },
  {
    type: Presets.FULL,
    title: `Full`,
    min: 0,
    max: Infinity,
    risk: PresetProfits.VERY_LOW,
    profit: PresetProfits.VERY_LOW
  }
];

const PresetTabs = ({ currencyA, currencyB, mintInfo }: RangeSidebarProps) => {
  const {
    preset,
    actions: { updateSelectedPreset, setFullRange }
  } = useLiquidityWizardState();

  const { onLeftRangeInput, onRightRangeInput } = useMintActionHandlers(
    mintInfo.noLiquidity
  );

  const isStablecoinPair = useMemo(() => {
    if (!currencyA || !currencyB) return false;

    // const stablecoins = [USDC.address, USDT.address, DAI.address];
    const stablecoins = ['', ''];

    return (
      stablecoins.includes(currencyA.wrapped.address.toLowerCase()) &&
      stablecoins.includes(currencyB.wrapped.address.toLowerCase())
    );
  }, [currencyA, currencyB]);

  const price = useMemo(() => {
    if (!mintInfo.price) return;

    return mintInfo.invertPrice
      ? mintInfo.price.invert().toSignificant(5)
      : mintInfo.price.toSignificant(5);
  }, [mintInfo]);

  const handlePresetRangeSelection = useCallback(
    (preset: any | null) => {
      if (!price) return;

      updateSelectedPreset(preset ? preset.type : null);

      if (preset && preset.type === Presets.FULL) {
        setFullRange();
      } else {
        onLeftRangeInput(preset ? String(+price * preset.min) : '');
        onRightRangeInput(preset ? String(+price * preset.max) : '');
      }
    },
    [
      onLeftRangeInput,
      onRightRangeInput,
      price,
      setFullRange,
      updateSelectedPreset
    ]
  );

  const presets = isStablecoinPair ? stablecoinsPreset : commonPresets;

  const onPresetSelect = useCallback(
    (range: PresetsArgs) => {
      if (preset == range.type) {
        handlePresetRangeSelection(null);
      } else {
        handlePresetRangeSelection(range);
      }
    },
    [preset, handlePresetRangeSelection]
  );

  // useEffect(() => {
  //   const commonPreset = presets.find(p => p.type === Presets.NORMAL);
  //
  //   if (commonPreset && price && !preset) {
  //     onPresetSelect(commonPreset);
  //   }
  // }, [onPresetSelect, preset, presets, price]);

  return (
    <HStack w="100%">
      {presets.map(range => {
        const isActive = preset === range.type;

        return (
          <Button
            key={range.title}
            py="10px"
            px="12px"
            fontFamily="'Inter', sans-serif"
            fontSize="12px"
            lineHeight="18px"
            letterSpacing="-0.18px"
            w="100%"
            color={isActive ? 'white' : 'neutral.200'}
            borderRadius="8px"
            border="0.5px solid"
            borderColor="neutral.500"
            bg={isActive ? 'neutral.500' : 'neutral.700'}
            transition="color 0.2s ease"
            _hover={{ color: 'white' }}
            onClick={() => onPresetSelect(range)}
          >
            {range.title}
          </Button>
        );
      })}
    </HStack>
  );
};

export default PresetTabs;
